import clsx from 'clsx';
import {
    type ComponentPropsWithoutRef,
    type ForwardedRef,
    forwardRef,
    type ReactNode,
} from 'react';
import { Button, type ButtonProps, Heading, type HeadingProps } from 'react-aria-components';

export interface SidebarSectionProps extends ComponentPropsWithoutRef<'div'> {}

/**
 * Used to display a label and a list of items or tree.
 * @example
 * ```tsx
 * <SidebarSection>
 *   <SidebarSectionHeading>My Label</SidebarSectionHeading>
 *   <SidebarTree />
 * </SidebarSection>
 */
export const SidebarSection = forwardRef(function SidebarSection(
    props: SidebarSectionProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    return (
        <div
            ref={ref}
            {...props}
            className={clsx('group/sidebar-section flex flex-col gap-0.5', props.className)}
        />
    );
});

export interface SidebarSectionHeadingProps extends HeadingProps {}

export const SidebarSectionHeading = forwardRef(function SidebarSectionHeading(
    props: SidebarSectionHeadingProps,
    ref: ForwardedRef<HTMLHeadingElement>
) {
    return (
        <Heading
            ref={ref}
            {...props}
            level={2}
            className={clsx(
                'flex items-center gap-1.5 truncate pl-1.5 font-medium text-muted text-ui-base leading-6',
                props.className
            )}
        />
    );
});

export interface SidebarSectionHeadingButtonProps extends ButtonProps {
    children: ReactNode;
}

export const SidebarSectionHeadingButton = forwardRef(function SidebarSectionHeadingButton(
    props: SidebarSectionHeadingButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
) {
    return (
        <Button
            ref={ref}
            {...props}
            className={clsx(
                '-ml-1.5 flex flex-1 items-center gap-1 rounded pl-1.5 leading-6',
                'text-muted hover:bg-standout-side',
                '-outline-offset-1 outline-1 outline-primary data-[focus-visible]:outline',
                props.className
            )}
        />
    );
});

export interface SidebarSectionHeadingActionsProps extends ComponentPropsWithoutRef<'div'> {
    children: ReactNode;
}

export const SidebarSectionHeadingActions = forwardRef(function SidebarSectionHeadingActions(
    props: SidebarSectionHeadingActionsProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    return (
        <div ref={ref} {...props} className={clsx('flex flex-1 justify-end', props.className)} />
    );
});
